import { motion } from 'framer-motion';
import { Lock, Gift, Check, Sparkles, TrendingUp } from 'lucide-react';
import { rarityOf, SLOT_THEME } from '../lib/classTheme';
import { compact } from '../lib/api';

/* ============================================================
   ITEM CARD — grid tile
   ============================================================ */
export default function ItemCard({
  item, selected, owned, equipped, locked, affordable = true,
  onClick, size = 'md', showStats = true, showPrice = false, badge,
}) {
  const r = rarityOf(item.rarity);
  const s = SLOT_THEME[item.slot];
  const dim = { sm: 'w-20', md: 'w-full', lg: 'w-full' }[size];

  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.95 }}
      whileHover={{ y: -3 }}
      className={`relative ${dim} rounded-2xl overflow-hidden border-2 bg-white text-left
        transition-all group ${selected ? 'ring-2 ring-offset-2' : ''}
        ${!item.available ? 'opacity-50' : ''}`}
      style={{
        borderColor: selected ? r.hex : `${r.hex}55`,
        boxShadow: selected ? r.shadowCss : undefined,
        '--tw-ring-color': r.hex,
      }}
    >
      {/* rarity glow background */}
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ background: `radial-gradient(circle at 50% 30%, ${r.hex}, transparent 70%)` }} />

      {/* image */}
      <div className="relative aspect-square bg-gradient-to-br from-ink-50 to-ink-100">
        {item.image || item.thumb ? (
          <img src={item.thumb || item.image} alt={item.name}
            className="w-full h-full object-cover" loading="lazy" draggable={false} />
        ) : (
          <div className="w-full h-full grid place-items-center text-3xl opacity-30">
            {item.icon || s?.icon}
          </div>
        )}

        {/* legendary shimmer */}
        {r.tier === 5 && (
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: 'linear-gradient(115deg, transparent 35%, rgba(255,255,255,.45) 50%, transparent 65%)' }}
            animate={{ x: ['-120%', '120%'] }}
            transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 2.5 }} />
        )}

        {/* epic pulse ring */}
        {r.tier === 4 && (
          <motion.div className="absolute inset-0 rounded-2xl pointer-events-none"
            style={{ boxShadow: `inset 0 0 22px ${r.hex}66` }}
            animate={{ opacity: [0.4, 0.85, 0.4] }}
            transition={{ duration: 2, repeat: Infinity }} />
        )}

        {/* status chips */}
        <div className="absolute top-1.5 left-1.5 flex flex-col gap-1">
          {equipped && (
            <span className="px-1.5 py-0.5 rounded-md bg-mint-500 text-white text-[8px] font-black shadow">
              EQUIPPED
            </span>
          )}
          {locked && (
            <span className="px-1 py-0.5 rounded-md bg-ink-900/80 text-white text-[8px] font-black shadow
              flex items-center gap-0.5">
              <Lock size={7} /> LOCKED
            </span>
          )}
          {badge}
        </div>

        {owned && !equipped && (
          <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-mint-500
            grid place-items-center text-white shadow">
            <Check size={11} strokeWidth={3} />
          </div>
        )}

        {selected && (
          <motion.div layoutId="itemSel"
            className="absolute inset-0 border-[3px] rounded-2xl pointer-events-none"
            style={{ borderColor: r.hex }} />
        )}

        {!item.available && (
          <div className="absolute inset-0 bg-ink-900/55 grid place-items-center">
            <span className="text-[9px] font-black text-white px-2 py-1 rounded bg-black/50">
              COMING SOON
            </span>
          </div>
        )}
      </div>

      {/* info */}
      <div className="relative p-2">
        <div className="flex items-center gap-1 mb-0.5">
          <span className="text-[10px]">{s?.icon}</span>
          <span className="text-[8px] font-black uppercase tracking-wide"
            style={{ color: r.hex }}>{r.label}</span>
        </div>

        <p className="font-bold text-[11px] leading-tight line-clamp-2 min-h-[26px]">
          {item.name}
        </p>

        {showStats && item.totalStats > 0 && (
          <div className="flex items-center gap-1 mt-1">
            <TrendingUp size={9} className="text-ink-400" />
            <span className="text-[9px] font-black text-ink-600">+{item.totalStats}</span>
            <span className="text-[8px] text-ink-400 font-bold">stats</span>
          </div>
        )}

        {showPrice && (
          <div className={`mt-1.5 flex items-center justify-center gap-1 py-1 rounded-lg text-[10px] font-black
            ${owned ? 'bg-mint-400/15 text-mint-700'
              : affordable ? 'bg-brand-50 text-brand-700' : 'bg-ink-100 text-ink-400'}`}>
            {owned ? '✓ OWNED' : <>💎 {compact(item.price)}</>}
          </div>
        )}
      </div>
    </motion.button>
  );
}

/* ============================================================
   ITEM DETAIL — bottom sheet content
   ============================================================ */
export function ItemDetail({ item, onEquip, onUnequip, onSell, onGift, busy }) {
  const r = rarityOf(item.rarity);
  const s = SLOT_THEME[item.slot];

  return (
    <div className="space-y-4">
      {/* hero image */}
      <div className="relative rounded-2xl overflow-hidden border-2"
        style={{ borderColor: r.hex, boxShadow: r.shadowCss }}>
        <div className="absolute inset-0 opacity-10"
          style={{ background: `radial-gradient(circle at 50% 40%, ${r.hex}, transparent 70%)` }} />
        <img src={item.image || item.thumb} alt={item.name}
          className="relative w-full aspect-square object-cover" />

        {r.tier >= 4 && (
          <motion.div className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(115deg, transparent 40%, ${r.hex}44 50%, transparent 60%)` }}
            animate={{ x: ['-100%', '100%'] }}
            transition={{ duration: 2.2, repeat: Infinity, repeatDelay: 1.5 }} />
        )}
      </div>

      {/* header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{s?.icon}</span>
          <span className="chip" style={{ background: `${r.hex}22`, color: r.hex }}>
            {r.label}
          </span>
          <span className="chip-ink">{s?.label}</span>
          {item.equipped && <span className="chip-mint">EQUIPPED</span>}
        </div>
        <h3 className="font-display text-xl font-extrabold">{item.name}</h3>
        {item.description && (
          <p className="text-sm text-ink-500 mt-1">{item.description}</p>
        )}
      </div>

      {/* stats */}
      {item.stats && Object.keys(item.stats).length > 0 && (
        <div className="card p-3.5">
          <div className="flex items-center justify-between mb-2.5">
            <p className="label !mb-0">Stat bonuses</p>
            <span className="chip-brand">+{item.totalStats} total</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {Object.entries(item.stats).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-ink-50">
                <span className="text-[11px] font-semibold text-ink-600 capitalize">
                  {k.replace('_', '')}
                </span>
                <span className="text-sm font-black" style={{ color: r.hex }}>+{v}</span>
              </div>
            ))}
          </div>
          {item.gearScore > 0 && (
            <div className="mt-2.5 pt-2.5 border-t border-ink-100 flex items-center justify-between">
              <span className="text-xs font-bold text-ink-500">Gear score</span>
              <span className="font-black text-sm" style={{ color: r.hex }}>{item.gearScore}</span>
            </div>
          )}
        </div>
      )}

      {/* meta */}
      <div className="grid grid-cols-2 gap-2 text-center">
        <Meta label="Source" value={
          { shop: '🛒 Bought', drop: '⚔️ Drop', gift: '🎁 Gift',
            starter: '✨ Starter', admin: '🛠️ Admin' }[item.source] || item.source
        } />
        <Meta label="Sell value" value={`💎 ${compact(item.sellPrice)}`} />
      </div>

      {item.giftedBy && (
        <p className="text-[11px] text-center text-ink-400 font-semibold">
          🎁 Gifted by @{item.giftedBy}
        </p>
      )}

      {/* actions */}
      <div className="grid grid-cols-2 gap-2">
        {item.equipped ? (
          <button onClick={onUnequip} disabled={busy} className="btn-ghost col-span-2">
            Unequip
          </button>
        ) : (
          <button onClick={onEquip} disabled={busy || item.locked} className="btn-primary col-span-2">
            <Sparkles size={15} /> Equip
          </button>
        )}

        <button onClick={onGift} disabled={busy || item.equipped || item.locked}
          className="btn-ghost">
          <Gift size={14} /> Gift
        </button>
        <button onClick={onSell} disabled={busy || item.equipped || item.locked}
          className="btn bg-red-50 text-red-600 font-bold">
          Sell 💎{compact(item.sellPrice)}
        </button>
      </div>

      {(item.equipped || item.locked) && (
        <p className="text-[11px] text-center text-ink-400 font-semibold">
          {item.locked ? '🔒 This item is locked in a pending gift'
            : 'Unequip first to gift or sell this item'}
        </p>
      )}
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div className="bg-ink-50 rounded-xl p-2.5">
      <p className="text-[9px] font-black text-ink-400 uppercase">{label}</p>
      <p className="font-bold text-sm mt-0.5">{value}</p>
    </div>
  );
}

/* ============================================================
   RARITY BADGE
   ============================================================ */
export function RarityBadge({ rarity, size = 'sm' }) {
  const r = rarityOf(rarity);
  const cls = size === 'lg' ? 'text-[11px] px-2.5 py-1' : 'text-[8px] px-1.5 py-0.5';
  return (
    <span className={`inline-block rounded-md font-black uppercase tracking-wide ${cls}`}
      style={{ background: `${r.hex}22`, color: r.hex }}>
      {r.label}
    </span>
  );
}